import React, { useState } from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  Flex, 
  Tab, 
  Tabs, 
  TabList, 
  TabPanel, 
  TabPanels,
  Code,
  Divider,
  Button,
  useColorModeValue
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import AleaPagination from '../../components/AleaPagination/AleaPagination';
import PageHeader from '../../components/PageHeader/PageHeader';

const AleaPaginationGuide: React.FC = () => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // State for different pagination examples
  const [basicPage, setBasicPage] = useState(1);
  const [manyPagesPage, setManyPagesPage] = useState(5);
  const [loadingPage, setLoadingPage] = useState(3);

  return (
    <Flex flexFlow="column">
      <PageHeader title={"Pagination"} />
      <Box p={8} bg="#fff" minH="100vh" borderRadius="20px">
        <Box maxW="1200px" mx="auto">
          <Flex mb={8} align="center">
            <Button
              leftIcon={<ChevronLeftIcon />}
              variant="ghost"
              onClick={() => navigate('/style-guide')}
              mr={4}
            >
              Back to Style Guide
            </Button>
          </Flex>

          <Text mb={6}>
            AleaPagination is a reusable pagination component that provides navigation controls for paginated data.
            It displays page numbers, navigation arrows, and result information with internationalization support.
          </Text>

          <Tabs variant="enclosed" mb={8}>
            <TabList>
              <Tab>Examples</Tab>
              <Tab>Props</Tab>
              <Tab>Usage</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <Box mb={8}>
                  <Heading size="md" mb={4}>Basic Pagination</Heading>
                  <Text mb={4}>
                    Basic pagination with moderate number of pages. Shows page numbers with navigation arrows.
                  </Text>
                  <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4}>
                    <AleaPagination
                      currentPage={basicPage}
                      totalPages={5}
                      total={100}
                      perPage={20}
                      onPageChange={setBasicPage}
                    />
                  </Box>
                </Box>

                <Divider my={6} />

                <Box mb={8}>
                  <Heading size="md" mb={4}>Many Pages</Heading>
                  <Text mb={4}>
                    Pagination with many pages. Shows condensed page numbers with ellipsis when needed.
                  </Text>
                  <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4}>
                    <AleaPagination
                      currentPage={manyPagesPage}
                      totalPages={25}
                      total={500}
                      perPage={20}
                      onPageChange={setManyPagesPage}
                    />
                  </Box>
                </Box>

                <Divider my={6} />

                <Box mb={8}>
                  <Heading size="md" mb={4}>Loading State</Heading>
                  <Text mb={4}>
                    Pagination in loading state. All buttons are disabled during loading.
                  </Text>
                  <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4}>
                    <AleaPagination
                      currentPage={loadingPage}
                      totalPages={10}
                      total={200}
                      perPage={20}
                      onPageChange={setLoadingPage}
                      isLoading={true}
                    />
                  </Box>
                </Box>



              </TabPanel>

              <TabPanel>
                <Box mb={8}>
                  <Heading size="md" mb={4}>Props</Heading>
                  <Box overflowX="auto">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Prop</th>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Type</th>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Default</th>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>currentPage</Code></td>
                          <td style={{ padding: '12px' }}><Code>number</Code></td>
                          <td style={{ padding: '12px' }}>-</td>
                          <td style={{ padding: '12px' }}>The current active page number</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>totalPages</Code></td>
                          <td style={{ padding: '12px' }}><Code>number</Code></td>
                          <td style={{ padding: '12px' }}>-</td>
                          <td style={{ padding: '12px' }}>Total number of pages available</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>total</Code></td>
                          <td style={{ padding: '12px' }}><Code>number</Code></td>
                          <td style={{ padding: '12px' }}>-</td>
                          <td style={{ padding: '12px' }}>Total number of items/records</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>perPage</Code></td>
                          <td style={{ padding: '12px' }}><Code>number?</Code></td>
                          <td style={{ padding: '12px' }}><Code>10</Code></td>
                          <td style={{ padding: '12px' }}>Number of items per page (optional, uses localStorage if not provided)</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>onPageChange</Code></td>
                          <td style={{ padding: '12px' }}><Code>{'(page: number) => void'}</Code></td>
                          <td style={{ padding: '12px' }}>-</td>
                          <td style={{ padding: '12px' }}>Callback function called when page changes</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>isLoading</Code></td>
                          <td style={{ padding: '12px' }}><Code>boolean</Code></td>
                          <td style={{ padding: '12px' }}><Code>false</Code></td>
                          <td style={{ padding: '12px' }}>Whether the pagination is in loading state</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>onPerPageChange</Code></td>
                          <td style={{ padding: '12px' }}><Code>{'(perPage: number) => void'}</Code></td>
                          <td style={{ padding: '12px' }}>-</td>
                          <td style={{ padding: '12px' }}>Optional callback function called when items per page changes</td>
                        </tr>
                      </tbody>
                    </table>
                  </Box>
                </Box>


              </TabPanel>

              <TabPanel>
                <Box mb={8}>
                  <Heading size="md" mb={4}>Basic Usage</Heading>
                  <Code p={4} borderRadius="md" w="100%" display="block" whiteSpace="pre">
{`// Step 1: Import the component
import AleaPagination from '../../components/AleaPagination/AleaPagination';

// Step 2: Set up state for pagination
const [currentPage, setCurrentPage] = useState(1);
const [isLoading, setIsLoading] = useState(false);

// Step 3: Calculate pagination values
const perPage = 20;
const total = 156; // Total number of items from your API
const totalPages = Math.ceil(total / perPage);

// Step 4: Handle page changes
const handlePageChange = async (page: number) => {
  setIsLoading(true);
  setCurrentPage(page);
  
  try {
    // Fetch data for the new page
    await fetchDataForPage(page);
  } catch (error) {
    console.error('Error fetching data:', error);
  } finally {
    setIsLoading(false);
  }
};

// Step 5: Use in JSX
<AleaPagination
  currentPage={currentPage}
  totalPages={totalPages}
  total={total}
  perPage={perPage}
  onPageChange={handlePageChange}
  isLoading={isLoading}
/>`}
                  </Code>
                </Box>



                <Divider my={6} />

                <Box mb={8}>
                  <Heading size="md" mb={4}>Translation Keys</Heading>
                  <Text mb={4}>
                    The component uses the following translation keys that should be defined in your i18n files:
                  </Text>
                  <Code p={4} borderRadius="md" w="100%" display="block" whiteSpace="pre">
{`// In your translation files (e.g., en.json)
{
  "pagination": {
    "showing": "Showing",
    "of": "of", 
    "results": "results"
  }
}`}
                  </Code>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Box>            
    </Flex>
  );
};

export default AleaPaginationGuide;